
import { BetHistoryEntity, BetHistoryEntitySelection } from '@/common/api/model';
import { OrderType, TransactionType } from '@/common/config';
import { formatDate } from '@/common/date';
import { round } from '@/common/utils';
import FaCheckSquare from '@/components/icons/FaCheckSquare.vue';
import FaRegSquare from '@/components/icons/FaRegSquare.vue';
import RadioEmpty from '@/components/icons/RadioEmpty.vue';
import RadioFilled from '@/components/icons/RadioFilled.vue';
import { Component, Prop, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import TransactionItemWarning from './TransactionItemWarning.vue';
import { SelectedEntity } from '@/common/types';

const userModule = namespace('user');
const getTitle = (firstSelection: BetHistoryEntitySelection) => {
  const isOutright = firstSelection.eventId.includes('match');
  return isOutright ? `${firstSelection.home} vs ${firstSelection.away}` : firstSelection.tournamentName;
};

@Component({
  components: {
  FaCheckSquare,
  FaRegSquare,
  RadioFilled,
  RadioEmpty,
  TransactionItemWarning,
  },
  filters: {
  formatDate (value: any) {
  return formatDate(value);
  },
  getTitle (firstSelection: BetHistoryEntitySelection) {
  return getTitle(firstSelection);
  },
  },
  })
export default class BetTransactionItem extends Vue {
    @Prop() readonly transaction!: BetHistoryEntity;
    @Prop() readonly selectedTransIssue!: BetHistoryEntity[];
    @Prop(Boolean) readonly isSingleTransactionSelectMode!: boolean;
    @Prop(Boolean) readonly isUserEventsSelection!: boolean;
    @Prop() selectIssueTransactions!: (item: BetHistoryEntity) => void;
    @Prop() handleSelectUserEventsSelections!: (item: BetHistoryEntitySelection) => void;
    @Prop() userEventsSelections!: BetHistoryEntitySelection[];
    @Prop({ default: '' }) readonly transactionType!: TransactionType;
    @Prop() expandTrans!: (shortId: string) => void;
    @Prop() expandedTrans!: string[];
    @Prop() handleExpandUserSelections!: (id: string) => void;
    @Prop() expandedUserEventsSelections!: string[];

    orderTypeMap = OrderType;

    get winningStatusMap () {
      return {
        0: this.$t('page.issue.bet.running'),
        5: this.$t('page.issue.bet.partial_win'),
        20: this.$t('page.issue.bet.won'),
        30: this.$t('page.issue.bet.lost'),
        40: this.$t('page.issue.bet.void'),
        90: this.$t('page.issue.bet.pending'),
      };
    }

    get userSelectionEventsStatusMap () {
      return {
        0: this.$t('page.issue.bet.running'),
        1: this.$t('page.issue.bet.won'),
        2: this.$t('page.issue.bet.lost'),
        3: this.$t('page.issue.bet.cancelled'),
        4: this.$t('page.issue.bet.void'),
        5: this.$t('page.issue.bet.win_and_refund_half'),
        6: this.$t('page.issue.bet.lost_and_refund_half'),
      };
    }

    @userModule.Getter('currency') currency!: string;
    @userModule.State('initSelectedEntity') initSelectedEntity!: SelectedEntity | null;

    get initialSelectedBet () {
      if (!this.initSelectedEntity || !this.initSelectedEntity.entity) {
        return null;
      }

      return this.initSelectedEntity.entity as BetHistoryEntity;
    }

    get isTransactionSelected () {
      if (this.initialSelectedBet && this.transaction.shortId === this.initialSelectedBet.shortId) {
        return true;
      }

      return this.selectedTransIssue.some((item) => item.shortId === this.transaction.shortId);
    }

    get calculateOdds () {
      if (this.transaction.orderType === 1 && this.transaction.selections.length === 1) {
        return round(+this.transaction.selections[0].odds, 2);
      } else if (this.transaction.orderType === 2) {
        const val = this.transaction.selections.reduce((acc: number, curr: BetHistoryEntitySelection) => {
          return acc * +curr.odds;
        }, 1);
        return round(val, 2);
      }
      return false;
    }

    get getTotalWinnings () {
      const { winningStatus, totalWinnings } = this.transaction;
      return winningStatus === 0 || winningStatus === 40 ? '--' : totalWinnings;
    }

    get getReturnClass () {
      const { winningStatus } = this.transaction;
      if (winningStatus === 5 || winningStatus === 20) {
        return 'winnings';
      } else if (winningStatus === 30) {
        return 'losses';
      } else {
        return null;
      }
    }

    get isTransactionSelectedByUser () {
      if (this.isUserEventsSelection) {
        return this.userEventsSelections.some((selection) => selection.id === this.transaction.selections[0].id);
      }

      return this.isTransactionSelected;
    }

    get idForTitle () {
      if (this.isUserEventsSelection) {
        return this.transaction.selections[0].eventId;
      }
      return this.transaction.shortId;
    }

    get handleExpandedTrans () {
      if (this.isUserEventsSelection) {
        return this.expandedUserEventsSelections.includes(this.transaction.selections[0]?.id);
      }
      return this.expandedTrans.includes(this.transaction.shortId);
    }

    handleExpandTrans () {
      if (this.isUserEventsSelection) {
        return this.handleExpandUserSelections(this.transaction.selections[0]?.id);
      }
      return this.expandTrans(this.transaction.shortId);
    }

    get betDetails () {
      return {
        stakeInfo: !this.isUserEventsSelection
          ? `${this.$t('page.issue.bet.total_stake')} (${this.currency}) ${this.transaction.totalStake}`
          : null,
        marketDescription: this.isUserEventsSelection && this.transaction.selections?.[0]?.marketDesc
          ? `${this.$t('page.issue.bet.market')}: ${this.transaction.selections[0].marketDesc}`
          : null,
        statusText: !this.isUserEventsSelection
          ? this.winningStatusMap[this.transaction.winningStatus]
          : this.userSelectionEventsStatusMap[this.transaction.selections?.[0]?.status],
      };
    }

    handleSelectTransaction () {
      if (this.isUserEventsSelection) {
        this.handleSelectUserEventsSelections(this.transaction.selections[0]);
        return false;
      }
      return this.selectIssueTransactions(this.transaction);
    }
}
